import React, { useMemo } from 'react';
import { get } from 'lodash';
import { mapAmenitiesToFeature } from '../config/amenityMap';
import { getRoundedRating } from '../utils/rating';

const DEFAULT_BASE_URL = 'https://www.sparefoot.com';

const paymentMethodsMap = {
	cashAccepted: 'Cash',
	creditCardsAccepted: 'Credit Card',
	checksAccepted: 'Check',
};

export function useSparefootSelfStorageSchema({
	facility,
	units,
	reviewCount,
	average,
	facilityUrl,
	baseUrl,
}) {
	return useMemo(() => {
		if (!facility || !facility.name) {
			return { facility: facility || {}, schemaPromotion: '', selfStorageSchema: null };
		}
		const amenityFeature = mapAmenitiesToFeature(facility) || [];
		const { promotion, cloudfrontUrl, images, phone, location } = facility;
		let imagesWithUrl = [];
		const resolvedUnits = units ?? facility.units;

		let schemaPromotion = promotion || '';
		if (!schemaPromotion.length && resolvedUnits?.length) {
			const unitPromotion = resolvedUnits.find(
				(unit) => unit.promotion?.length
			);
			schemaPromotion = get(unitPromotion, 'promotion', '');
		}

		const resolvedBaseUrl = baseUrl || DEFAULT_BASE_URL;

		if (images?.length) {
			imagesWithUrl = images.map(
				(image) =>
					`${cloudfrontUrl ? 'https:' : ''}${
						cloudfrontUrl ?? resolvedBaseUrl
					}/large-compress/${image}`,
			);
		}

		const resolvedReviewCount =
			reviewCount ?? facility.reviewMetadata?.count;
		const fallbackAverage = facility.reviewMetadata?.average;
		const resolvedAverage =
			average ??
			(fallbackAverage != null
				? getRoundedRating(fallbackAverage)
				: undefined);
		const resolvedFacilityUrl =
			facilityUrl ?? facility.url?.facility ?? '';

		// Build SelfStorage JSON-LD schema. This is used by search result cards and
		// "facilities near you" modules, which may not pass baseUrl explicitly.
		const schemaUrl = `${resolvedBaseUrl}${resolvedFacilityUrl}`;
		const selfStorageSchema = {
			'@context': 'https://schema.org',
			'@type': 'SelfStorage',
			name: facility.name,
			url: schemaUrl,
			priceRange: '$-$$',
			address: {
				'@type': 'PostalAddress',
				addressLocality: location?.city,
				addressRegion: location?.state,
				postalCode: location?.zip,
				streetAddress: location?.streetAddress,
				addressCountry: { '@type': 'Country', name: 'US' },
			},
			geo: {
				'@type': 'GeoCoordinates',
				latitude: location?.latitude,
				longitude: location?.longitude,
			},
			currenciesAccepted: 'USD',
			hasMap: facility?.staticMapUrl,
		};

		if (phone && phone.length) {
			selfStorageSchema.telephone = phone;
		}

		if (schemaPromotion && schemaPromotion.length) {
			const priceMatches = schemaPromotion.includes('$')
				? schemaPromotion.match(/\$[\d,]+(?:\.\d{2})?/g)
				: null;
			const extractedPrice =
				priceMatches && priceMatches.length
					? priceMatches[0].replace('$', '').replace(/,/g, '')
					: null;

			selfStorageSchema.makesOffer = {
				'@type': 'Offer',
				name: schemaPromotion,
				description: schemaPromotion,
				...(extractedPrice && {
					price: extractedPrice,
					priceCurrency: 'USD',
				}),
				availability: 'https://schema.org/InStock',
			};
		}

		if (imagesWithUrl.length) {
			selfStorageSchema.image = imagesWithUrl.map((url) => ({
				'@type': 'ImageObject',
				url,
			}));
		}

		if (amenityFeature.length) {
			selfStorageSchema.amenityFeature = amenityFeature;
		}

		const billingAmenities = facility.billingAmenities;
		if (billingAmenities && typeof billingAmenities === 'object') {
			const methods = Object.entries(billingAmenities)
				.filter(
					([key, item]) =>
						item?.hasAttribute === true && paymentMethodsMap[key],
				)
				.map(([key]) => paymentMethodsMap[key]);

			if (methods.length) {
				selfStorageSchema.paymentAccepted = methods;
			}
		}

		const aggCount = get(facility, 'reviewMetadata.count', 0);
		const aggAvg = get(facility, 'reviewMetadata.average');
		if (aggCount > 0) {
			selfStorageSchema.aggregateRating = {
				'@type': 'AggregateRating',
				ratingCount: aggCount,
				ratingValue: aggAvg,
			};
		}

		return {
			facility: {
				...facility,
				name: facility.name,
				...(imagesWithUrl.length > 0
					? { image: imagesWithUrl }
					: {}),
				location: {
					...location,
					streetAddress1: location.streetAddress,
				},
				facilityPhone: phone,
				imagesWithUrl,
				reviews: {
					...facility.reviews,
					number: resolvedReviewCount,
					average: resolvedAverage,
				},
				schemaUrl,
				amenityFeature,
			},
			schemaPromotion,
			selfStorageSchema: JSON.stringify(selfStorageSchema),
		};
	}, [facility, units, reviewCount, average, facilityUrl, baseUrl]);
}

function withSparefootSelfStorageSchema(WrappedComponent) {
	const WithSparefootSelfStorageSchema = (props) => {
		const hookResult = useSparefootSelfStorageSchema(props);
		return (
			<WrappedComponent
				{...props}
				{...hookResult}
			/>
		);
	};
	WithSparefootSelfStorageSchema.displayName = `withSparefootSelfStorageSchema(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
	return WithSparefootSelfStorageSchema;
}

export default withSparefootSelfStorageSchema;
